// shared/src/oracle/token-relations.ts
// Slice 2 — wrapped/bridged token ↔ NATIVE corroboration (universal-source §3d).
//
// WHY: every held wrapped token is priced AS ITSELF under its own $-key ($WBTC is NOT
// $BTC). That is deliberate — a depeg must be VISIBLE, not silently papered over by the
// native's price. But "priced as itself" only pays off if something WATCHES the spread:
// otherwise a wrapper that breaks its peg just reports a wrong price confidently.
// A relation says: "$WBTC SHOULD equal $BTC × ratio, within tol."
//
// Two uses, both in the resolver (§3d):
//   1. CORROBORATE — compute the observed ratio vs the expected one; beyond `tol`, stamp
//      the canonical's provenance so the deviation is loud instead of invisible.
//   2. FALLBACK — when a key's OWN sources all fail (value null), the native × ratio is a
//      LAST-GOOD-quality estimate rather than nothing. Flagged, never silent.
//
// RATIO SOURCE — every live relation is 'fixed': the wrapper is 1:1 by construction
// (WBTC↔BTC custodial mint; WORMSOL↔SOL bridge lock), so the ratio is a constant. (The
// accruing 'onchain' variant existed only for wstETH, whose relation was retired
// 2026-07-17 — see TOKEN_RELATIONS below.)

/** How a relation's expected ratio is obtained. */
export type RatioSource = { readonly kind: 'fixed'; readonly ratio: number };

export interface TokenRelation {
  /** The wrapped/bridged key being watched ('$WBTC'). */
  readonly key:        string;
  /** The native/reference key it is related to ('$BTC'). */
  readonly nativeKey:  string;
  /** expected key ≈ nativeKey × ratio. */
  readonly ratio:      RatioSource;
  /** Fractional deviation beyond which the relation is FLAGGED (0.005 = 0.5%). */
  readonly tol:        number;
  /** May the native × ratio stand in when the key's own sources all fail? FALSE for a
   *  wrapper whose whole point is that it can decouple — a fallback would then invent
   *  exactly the number we are trying to detect as wrong. */
  readonly allowFallback: boolean;
}

// SEED-ONLY as of #93 (migration 0111): the LIVE source of truth is now oracle_relations_config
// (readEnabledRelations), which the producer + breach-log filter read. This constant is the values
// migration 0111 seeds the table with — editing it changes nothing at runtime; edit the DB rows via
// the /oracle Relations tab (PATCH /api/oracle/relations/config). Kept as the documented default +
// for unit tests. RELATION_BY_KEY below has no runtime consumer post-#93 (retained for tests).
export const TOKEN_RELATIONS: ReadonlyArray<TokenRelation> = [
  // WBTC is 1:1-minted against custodied BTC. A sustained gap = a custody/redemption
  // scare, which is EXACTLY the event worth flagging — so watch it tightly.
  // Fallback ALLOWED: if the DEX/vendor legs die, real BTC is a sound stand-in for a
  // 1:1 wrapper, and the flag still fires if they later disagree.
  { key: '$WBTC', nativeKey: '$BTC', ratio: { kind: 'fixed', ratio: 1 }, tol: 0.01, allowFallback: true },

  // Wormhole-bridged SOL, 1:1 locked. Same reasoning as WBTC; a bridge failure is the
  // event to catch. Its own legs are thin (1 pool + alchemy), so fallback earns its keep.
  { key: '$WORMSOL', nativeKey: '$SOL', ratio: { kind: 'fixed', ratio: 1 }, tol: 0.01, allowFallback: true },

  // wstETH RELATION RETIRED 2026-07-17 — PERMANENTLY, with no replacement relation.
  // The old check used nativeKey $ETH × stEthPerToken, which reduces observed/expected to
  // the stETH/ETH market basis, so a normal stETH discount tripped a false "wstETH depeg".
  // A $STETH-native version is NOT the fix either: wstETH has its own price discovery in its
  // pools and legitimately floats vs stETH × wrap-rate (and stETH itself floats vs ETH), so
  // ANY peg/corroboration reference measures a real market basis, not a source problem.
  // wstETH is priced AS ITSELF from its pool, with NO peg watch — this is final, do not
  // re-add a relation for it. (wstETH was the only 'onchain' accruing-wrapper case, so the
  // LIDO_ST_ETH_PER_TOKEN + decodeFixedPointRate + onchain-branch machinery was removed
  // with it.)
] as const;

export const RELATION_BY_KEY: ReadonlyMap<string, TokenRelation> = new Map(
  TOKEN_RELATIONS.map((r) => [r.key, r]),
);

export interface RelationCheck {
  readonly key:            string;
  readonly nativeKey:      string;
  readonly expectedRatio:  number;
  readonly observedRatio:  number;
  readonly deviationPct:   number;   // signed fraction: (observed − expected) / expected
  readonly tol:            number;
  readonly breached:       boolean;
  readonly ratioLabel:     string;
}

/** Pure: compare a resolved wrapped price against native × ratio.
 *  Returns null when either side is unavailable — absence is NOT a breach
 *  (feedback_no_fallbacks: never invent a verdict from missing data). */
export function checkRelation(
  relation:    TokenRelation,
  keyValue:    number | null,
  nativeValue: number | null,
  ratio:       number | null,
): RelationCheck | null {
  if (keyValue == null || nativeValue == null || ratio == null) return null;
  if (!(keyValue > 0) || !(nativeValue > 0) || !(ratio > 0)) return null;
  const observedRatio = keyValue / nativeValue;
  const deviationPct  = (observedRatio - ratio) / ratio;
  return {
    key:           relation.key,
    nativeKey:     relation.nativeKey,
    expectedRatio: ratio,
    observedRatio,
    deviationPct,
    tol:           relation.tol,
    breached:      Math.abs(deviationPct) > relation.tol,
    ratioLabel:    `fixed:${relation.ratio.ratio}`,
  };
}
